import type { ValueOf } from '@travel-planner/shared/types';
import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';

// Search-field values are duplicated here for back-compat (the toolbar
// imports the constant directly); the same enum on the URL side lives in
// `apps/operations/web/src/features/operations-filters/model/search-params.ts`.
export const OPERATIONS_SEARCH_FIELD = {
  Optigest: 'optigest',
  Traveler: 'traveler',
  Customer: 'customer',
  Supplier: 'supplier',
} as const;

export type OperationsSearchField = ValueOf<typeof OPERATIONS_SEARCH_FIELD>;

export const OPERATIONS_VIEW = {
  Active: 'active',
  History: 'history',
} as const;

export type OperationsView = ValueOf<typeof OPERATIONS_VIEW>;

export const OPERATIONS_FLAGGED = {
  Flagged: 'flagged',
  PreFlagged: 'preFlagged',
  FailedMsg: 'failedMsg',
} as const;

export type OperationsFlagged = ValueOf<typeof OPERATIONS_FLAGGED>;

export const OPERATIONS_HAS_ATTACHMENTS = {
  Any: 'any',
  With: 'with',
  Without: 'without',
} as const;

export type OperationsHasAttachments = ValueOf<
  typeof OPERATIONS_HAS_ATTACHMENTS
>;

// Operations filter state — toolbar / sub-tab / secondary filters that
// stay client-only. The agent-targetable filters (search, searchField,
// itineraries, startDate/endDate, contentCategory, country,
// itineraryPriorityLevel, operationStatuses, offset, limit) live in URL
// search params under `OperationsSearch` and are written via
// `useNavigate({ search: ... })` from the operations layout. See
// `apps/operations/web/src/features/operations-filters/`.
export type OperationsFilterState = {
  view: OperationsView;
  // toolbar bullets (Flagged / Pre-Flagged / Failed Messages)
  flagged?: OperationsFlagged;
  // sub-tab category overlay (set by `subTabContentFilter`)
  contentSubcategory?: string[];
  // sub-tab + secondary filter chips
  languageCode?: string[];
  venues?: string[];
  transportType?: string[];
  serviceType?: string[];
  labels?: string[];
  // Ticket "type" = the ticket subitem's `details.priority`
  // (regular | fast-track | private) — Tickets tab filter, legacy ProductCell
  // parity (TP-4077).
  ticketTypes?: string[];
  hasAttachments?: OperationsHasAttachments;
  // serviceProvider has no DB column today and lives here only to persist
  // the user's selection across sessions. Phase 5 (supplier + tour-operator
  // domain) will wire it through the API.
  serviceProvider?: string;
  guideServiceType?: string[];
  // meta
  hasChanged: boolean;
};

export const initialOperationsFilterState: OperationsFilterState = {
  view: OPERATIONS_VIEW.Active,
  flagged: undefined,
  contentSubcategory: undefined,
  languageCode: undefined,
  venues: undefined,
  transportType: undefined,
  serviceType: undefined,
  labels: undefined,
  ticketTypes: undefined,
  hasAttachments: undefined,
  serviceProvider: undefined,
  guideServiceType: undefined,
  hasChanged: false,
};

type OperationsFilterStore = OperationsFilterState & {
  setFilter: (patch: Partial<OperationsFilterState>) => void;
  reset: () => void;
};

const arraysShallowEqual = (
  a: readonly unknown[] | undefined,
  b: readonly unknown[] | undefined
): boolean => {
  if (a === b) return true;
  if (!a || !b) return false;
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    if (a[i] !== b[i]) return false;
  }
  return true;
};

const trackHasChanged = (
  prev: OperationsFilterState,
  patch: Partial<OperationsFilterState>
): boolean => {
  if (prev.hasChanged) return true;
  for (const rawKey of Object.keys(patch)) {
    const key = rawKey as keyof OperationsFilterState;
    if (key === 'hasChanged') continue;
    const prevValue = prev[key];
    const nextValue = patch[key];
    if (Array.isArray(prevValue) || Array.isArray(nextValue)) {
      if (
        !arraysShallowEqual(
          prevValue as readonly unknown[] | undefined,
          nextValue as readonly unknown[] | undefined
        )
      ) {
        return true;
      }
    } else if (prevValue !== nextValue) {
      return true;
    }
  }
  return false;
};

// v1 had migrated-out keys (search, searchField, startDate, endDate,
// itineraries, contentCategory, country, itineraryPriorityLevel,
// operationStatuses, offset, limit). Returning users still have these
// in `sessionStorage`; Zustand's `persist` deep-merges the persisted
// payload with the initialiser and does NOT strip unknown keys, so we
// strip them here on hydration.
const V1_MIGRATED_OUT_KEYS = new Set([
  'offset',
  'limit',
  'startDate',
  'endDate',
  'search',
  'searchField',
  'itineraries',
  'contentCategory',
  'country',
  'operationStatuses',
  'itineraryPriorityLevel',
]);

export const useOperationsFilterStore = create<OperationsFilterStore>()(
  persist(
    (set) => ({
      ...initialOperationsFilterState,
      setFilter: (patch) =>
        set((state) => ({
          ...patch,
          hasChanged: trackHasChanged(state, patch),
        })),
      reset: () => set({ ...initialOperationsFilterState }),
    }),
    {
      name: 'operations-filter',
      storage: createJSONStorage(() => window.sessionStorage),
      version: 2,
      migrate: (persisted, fromVersion) => {
        if (!persisted || typeof persisted !== 'object') {
          return persisted as OperationsFilterStore;
        }
        if (fromVersion < 2) {
          const cleaned = Object.fromEntries(
            Object.entries(persisted as Record<string, unknown>).filter(
              ([k]) => !V1_MIGRATED_OUT_KEYS.has(k)
            )
          );
          return cleaned as OperationsFilterStore;
        }
        return persisted as OperationsFilterStore;
      },
    }
  )
);
